import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { Registration } from '@/types/registration.types';
import Container from '@ui/container/container';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import Text from '@ui/text/text/text';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { ChangeEvent } from 'react';

export default function RegistrationLimitToggle() {
  const dispatch = useAppDispatch();
  const { registrationSettings } = useComputedRegistrationSettings();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const registrationLimitIsOn = registrationSettings?.registration_limit_is_on;
  const registrationLimit = registrationSettings?.registration_limit;

  const toggleRegistrationLimit = (value: boolean, on: boolean) => {
    if (!registrationSettings) return;
    const updatedSettings: Registration = {
      ...registrationSettings,
      registration_limit_is_on: on,
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  const setRegistrationLimit = (evt: ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value ? Number(evt.target.value) : null;
    if (!registrationSettings) return;
    const updatedSettings: Registration = {
      ...registrationSettings,
      registration_limit: value,
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  return (
    <Container design="GlobalCont1" borderRadius="12px" padding="16px" className="flex fd-column gap-12">
      <div className="flex ai-center jc-space-between">
        <Text tag="h6" className="H6">
          Registration limit
        </Text>
        <div>
          <SwitchV2
            loading={!registrationSettings || savingWorkingProject}
            value={!!registrationLimitIsOn}
            on={!!registrationLimitIsOn}
            onClick={toggleRegistrationLimit}
          />
        </div>
      </div>
      <OptionalComponent display={registrationLimitIsOn}>
        <InputWithOverlay
          inputHeight="36px"
          inputProps={{
            type: 'number',
            defaultValue: registrationLimit || '',
            placeholder: 'Digits only, e.g. 10000',
            disabled: !registrationLimitIsOn || savingWorkingProject,
            onKeyDown: (evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault(),
            style: {
              backgroundColor: 'var(--global-cont-2)',
            },
            onBlur: setRegistrationLimit,
          }}
        />
      </OptionalComponent>
    </Container>
  );
}
